import React from 'react';
import { ArrowRightIcon, KeyRoundIcon, LightbulbIcon } from 'lucide-react';
import { SmartLink } from '../SmartLink';
import type { ArticleBlock } from '../../types/article';
const body = 'text-[18px] leading-[1.75] text-ink-soft';
export function ArticleBlockView({
  block
}: {
  block: ArticleBlock;
}) {
  switch (block.type) {
    case 'p':
      return <p className={body} data-id="element-55" data-mp="21gesbys3jlfx">{block.text}</p>;
    case 'h3':
      return <h3 className="pt-2 text-[21px] font-semibold tracking-[-0.02em] text-ink" data-id="element-56" data-mp="1dr2j4ywrrddm">{block.text}</h3>;
    case 'ul':
      return <ul className={`${body} list-disc space-y-2 pl-6 marker:text-brand`} data-id="element-57" data-mp="18sr7cimwyssr">
          {block.items.map(item => <li key={item} className="pl-1" data-id="element-58" data-mp="1nhng6hwso9uf">
              {item}
            </li>)}
        </ul>;
    case 'ol':
      return <ol className={`${body} list-decimal space-y-2 pl-6 marker:font-semibold marker:text-ink`} data-id="element-59" data-mp="uyhs7n0mricg">
          {block.items.map(item => <li key={item} className="pl-1" data-id="element-60" data-mp="pyc76pov8u3c">
              {item}
            </li>)}
        </ol>;
    case 'takeaway':
      return <aside className="rounded-[12px] border-l-4 border-brand bg-brand-soft px-5 py-5 sm:px-6" data-id="element-61" data-mp="1ey2jdtdw8odi">
          <p className="flex items-center gap-2 text-[14px] font-semibold text-[#0F5E99]" data-id="element-62" data-mp="1jpc88rrmbnoc">
            <KeyRoundIcon className="h-4 w-4" aria-hidden="true" data-id="element-63" data-mp="1nrfl9ktxr0bq" />
            Key takeaway
          </p>
          <p className="mt-2 text-[17px] font-medium leading-[1.6] text-ink" data-id="element-64" data-mp="r1otvugxxta5">{block.text}</p>
        </aside>;
    case 'tip':
      return <aside className="flex gap-3 rounded-[12px] border border-canvas-line bg-canvas-warm px-5 py-4" data-id="element-65" data-mp="w244ie9u8mof">
          <LightbulbIcon className="mt-1 h-5 w-5 shrink-0 text-[#9A6700]" aria-hidden="true" data-id="element-66" data-mp="103y0wad034is" />
          <p className="text-[16px] leading-[1.6] text-ink" data-id="element-67" data-mp="14ylgfjq10ol8">
            <span className="font-semibold" data-id="element-68" data-mp="2bfytk0czjd3t">Tip: </span>
            {block.text}
          </p>
        </aside>;
    case 'image':
      return <figure data-id="element-69" data-mp="gdln6auu4zzw">
          <img src={block.src} alt={block.alt} loading="lazy" className="w-full rounded-[12px] object-cover" data-id="element-70" data-mp="gd2gxrejs01p" />
          <figcaption className="mt-3 text-[14px] text-ink-muted" data-id="element-71" data-mp="ccs0fkt4pr8l">{block.caption}</figcaption>
        </figure>;
    case 'table':
      return <figure data-id="element-72" data-mp="q9oz28fhod01">
          <div className="overflow-x-auto rounded-[12px] border border-canvas-line" data-id="element-73" data-mp="lcotrzqgre9z">
            <table className="w-full min-w-[520px] border-collapse text-left text-[15px]" data-id="element-74" data-mp="lavlkg07clzv">
              <thead className="bg-canvas-warm" data-id="element-75" data-mp="3tqfbb51nzma6">
                <tr data-id="element-76" data-mp="3ou6qbb0scyv3">
                  {block.head.map(h => <th key={h} scope="col" className="px-4 py-3 font-semibold text-ink" data-id="element-77" data-mp="7cx7zctpgtlf">
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-78" data-mp="3b20j4efm227s">
                {block.rows.map((row, i) => {
                const last = i === block.rows.length - 1 && row[0] === 'Total';
                return <tr key={row.join('|')} className={`border-t border-canvas-line ${last ? 'font-semibold text-ink' : 'text-ink-soft'}`} data-id="element-79" data-mp="360tciip7f92p" data-mpi={i}>
                      {row.map((cell, j) => <td key={j} className={`px-4 py-3 ${j > 0 ? 'tabular-nums' : ''}`} data-id="element-80" data-mp="1qcwiwbvowvxr" data-mpi={j}>
                          {cell}
                        </td>)}
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
          {block.caption && <figcaption className="mt-3 text-[14px] text-ink-muted" data-id="element-81" data-mp="249a33srlzbfx">{block.caption}</figcaption>}
        </figure>;
    case 'quote':
      return <figure className="border-l-4 border-ink py-1 pl-6" data-id="element-82" data-mp="1v9mxeurujjh2">
          <blockquote className="display text-[26px] font-semibold text-ink sm:text-[30px]" data-id="element-83" data-mp="209rd4ikxka68">“{block.text}”</blockquote>
          {block.cite && <figcaption className="mt-3 text-[15px] text-ink-muted" data-id="element-84" data-mp="juhsdk48t8ua">— {block.cite}</figcaption>}
        </figure>;
    case 'cta':
      return <aside className="flex flex-col gap-4 rounded-[14px] bg-ink px-6 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-8" data-id="element-85" data-mp="os9j4at5ioww">
          <div data-id="element-86" data-mp="zd0ww45l56ux">
            <p className="text-[18px] font-semibold text-white" data-id="element-87" data-mp="zchphhdka0v5">{block.title}</p>
            <p className="mt-1 text-[15px] leading-[1.55] text-white/70" data-id="element-88" data-mp="udy3jgyywp2s">{block.text}</p>
          </div>
          <SmartLink href={block.href} className="inline-flex h-12 shrink-0 items-center justify-center gap-1.5 rounded-[7px] bg-brand px-5 text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover" data-id="element-89" data-mp="qdnmn99n0e95">
            {block.label}
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-90" data-mp="1e1nmcyoxfezv" />
          </SmartLink>
        </aside>;
  }
}